import React, {useEffect, useState} from 'react';
import {Modal, ModalHeader, ModalBody, ModalFooter, Button} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {SpinnerButton} from '../../SpinnerButton';
import {ClientMemoryApi} from '../../../api/clientMemoryApi';

/**
 * Borrar un documento de la Memoria — y decidir si se va solo o con lo que se extrajo de él.
 *
 * Reemplaza al `YesNoModal` genérico que había en las dos bibliotecas. El diálogo viejo avisaba
 * «los hechos ya extraídos no se borran» y ahí terminaba: el usuario leía la consecuencia pero no
 * tenía ninguna salida. El caso que lo motiva (card VLtk3vc7) es el documento EQUIVOCADO —material
 * de otro cliente, un borrador viejo—, donde lo que hay que sacar de la memoria es justamente lo
 * que se extrajo, y hacerlo a mano significaba cazar los hechos uno por uno en la cola.
 *
 * Las cuatro decisiones de diseño, y por qué:
 *
 *  1. **Dos opciones visibles, no un checkbox.** Un tilde esconde la mitad de la decisión debajo de
 *     un botón que igual dice «Sí»: hay que leer la casilla Y el botón para saber qué va a pasar.
 *     Con dos opciones, cada una dice su consecuencia completa y el botón confirma la que está
 *     elegida. Es el mismo objeto que el selector de tipo de material (`cmem-kind-option`).
 *  2. **«Solo el documento» viene elegida.** Es lo que esta pantalla hacía hasta hoy y es la menos
 *     destructiva: la opción nueva se ofrece, no se impone.
 *  3. **Números reales, traídos del backend.** «Se borran 13 hechos» sólo se puede decir si son 13.
 *     `deletion-impact` separa los que se borran de los que sobreviven porque otro material también
 *     los dice — prometer un número y ejecutar otro sería peor que no decir ninguno. Mientras el
 *     conteo viaja, la opción se ofrece sin cifras; si el pedido falla, también (nunca se inventa).
 *  4. **Sin nada extraído no hay pregunta.** Un documento que no produjo hechos ni resumen se borra
 *     con una confirmación simple: obligar a elegir entre dos caminos idénticos es ruido.
 */
export function DocumentDeleteModal({isOpen, doc, clientId, toggle, onConfirm, isSaving}) {
    const {t} = useTranslation();
    const [withExtracted, setWithExtracted] = useState(false);
    const [impact, setImpact] = useState(null);
    const [loadingImpact, setLoadingImpact] = useState(false);

    // El impacto se pide CADA vez que se abre, y no se cachea por documento: entre dos aperturas
    // pudo aprobarse, rechazarse o fusionarse un hecho, y un número viejo en una acción
    // irreversible es peor que ninguno.
    useEffect(() => {
        if (!isOpen || !doc) return undefined;
        let cancelled = false;
        setWithExtracted(false);
        setImpact(null);
        setLoadingImpact(true);
        ClientMemoryApi.getDeletionImpact(doc.id, clientId)
            .then((data) => {
                if (cancelled) return;
                setImpact(data && data.impact ? data.impact : null);
                setLoadingImpact(false);
            })
            .catch(() => {
                if (cancelled) return;
                setLoadingImpact(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, doc, clientId]);

    if (!doc) return null;

    const title = doc.title || t('client_memory.documents.untitled');
    // «Sabemos que no hay nada» exige haber recibido el conteo: sin él (cargando, o el pedido
    // falló) se ofrece la elección igual, porque suponer que no hay nada extraído esconde la
    // opción justo en el caso en que más se necesita.
    const nothingExtracted = !!impact && !impact.facts && !impact.facts_kept
        && !impact.notes && !impact.events;

    // Lo que se va a ejecutar de verdad. Con el documento sin nada extraído las dos opciones son
    // la misma cosa, y la pantalla ni siquiera las ofrece: la cascada se apaga sola.
    const cascading = withExtracted && !nothingExtracted;
    const confirm = () => onConfirm(cascading);

    // `role="none"` en el `<li>`: el `role="radiogroup"` del `<ul>` no «posee» a sus radios si hay
    // un elemento con semántica propia en el medio, así que para un lector de pantalla el grupo
    // quedaba vacío (review C-borrado-12).
    const option = (value, labelKey, description) => (
        <li role="none" key={String(value)}>
            <label className={`cmem-kind-option ${withExtracted === value ? '-selected' : ''}`}>
                <input type="radio" name="cmem-doc-delete" checked={withExtracted === value}
                       onChange={() => setWithExtracted(value)}/>
                <span className="cmem-kind-option-text">
                    <span className="cmem-kind-option-title"><strong>{t(labelKey)}</strong></span>
                    <span className="cmem-kind-option-desc">{description}</span>
                </span>
            </label>
        </li>
    );

    // Lo que se va con el documento, dicho en las unidades que el usuario reconoce. Sin conteo
    // (todavía no llegó, o falló) se describe en palabras: es cierto igual.
    const cascadeDescription = () => {
        if (!impact) return t('client_memory.documents.delete_with_extracted_desc_unknown');
        const parts = [];
        if (impact.facts) {
            parts.push(t(impact.facts === 1
                ? 'client_memory.documents.delete_count_facts_one'
                : 'client_memory.documents.delete_count_facts_many', {n: impact.facts}));
        }
        if (impact.notes) {
            parts.push(t(impact.notes === 1
                ? 'client_memory.documents.delete_count_notes_one'
                : 'client_memory.documents.delete_count_notes_many', {n: impact.notes}));
        }
        if (impact.events) {
            parts.push(t(impact.events === 1
                ? 'client_memory.documents.delete_count_events_one'
                : 'client_memory.documents.delete_count_events_many', {n: impact.events}));
        }
        if (!parts.length) return t('client_memory.documents.delete_with_extracted_desc_empty');
        return t('client_memory.documents.delete_with_extracted_desc', {items: parts.join(', ')});
    };

    return (
        /* Durante el borrado el diálogo no se cierra por ningún camino: el botón Cancelar ya
           estaba deshabilitado, pero Escape y el clic en el fondo seguían vivos y dejaban una
           acción irreversible corriendo sin nada en pantalla (review C-borrado-12). */
        <Modal isOpen={isOpen} toggle={isSaving ? undefined : toggle}
               keyboard={!isSaving} backdrop={isSaving ? 'static' : true}>
            <ModalHeader toggle={isSaving ? undefined : toggle}>
                {t('client_memory.documents.delete_title')}
            </ModalHeader>
            <ModalBody>
                <div className="modal-section cmem-kind-modal">
                    {/* `escapeValue: false` porque i18next escapa los valores interpolados y React
                        después imprime el resultado como TEXTO: un título con `&` salía como
                        «Kumel Inn &amp; Outing» — el nombre del material, mal escrito, en una
                        confirmación irreversible (QA-VIS-borrado-02). El valor no es HTML: lo
                        renderiza React, que ya escapa. */}
                    <p className="cmem-kind-lead">
                        {t('client_memory.documents.delete_lead',
                            {title, interpolation: {escapeValue: false}})}
                    </p>

                    {nothingExtracted ? (
                        <p className="cmem-kind-lead">
                            {t('client_memory.documents.delete_nothing_extracted')}
                        </p>
                    ) : (
                        <ul className="cmem-kind-options"
                            role="radiogroup"
                            aria-label={t('client_memory.documents.delete_title')}>
                            {option(false, 'client_memory.documents.delete_only_doc',
                                t('client_memory.documents.delete_only_doc_desc'))}
                            {option(true, 'client_memory.documents.delete_with_extracted',
                                cascadeDescription())}
                        </ul>
                    )}

                    {/* El número que evita la promesa falsa. Va afuera de la opción y siempre
                        visible: explica por qué la cuenta que se ejecuta puede no coincidir con
                        la cantidad de hechos que la fila del documento muestra. */}
                    {!!impact && !!impact.facts_kept && !nothingExtracted && (
                        <p className="cmem-delete-kept">
                            {t(impact.facts_kept === 1
                                ? 'client_memory.documents.delete_kept_one'
                                : 'client_memory.documents.delete_kept_many', {n: impact.facts_kept})}
                        </p>
                    )}

                    <p className="cmem-delete-warning">
                        {t('client_memory.documents.delete_irreversible')}
                    </p>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle} disabled={isSaving}>
                    {t('common.cancel')}
                </Button>
                {isSaving || (loadingImpact && withExtracted) ? (
                    /* El spinner cubre DOS esperas: el borrado en curso y el conteo que todavía
                       viaja. Antes la segunda dejaba un botón rojo apagado, sin spinner y sin
                       texto: con el backend lento eso son decenas de segundos de pantalla muda
                       (QA-VIS-borrado-07, medido en más de 60 s). */
                    <SpinnerButton/>
                ) : (
                    // El botón NOMBRA lo que va a hacer: un «Sí» obliga a releer la opción elegida
                    // para saber qué se está confirmando, y acá no hay segunda oportunidad.
                    <button type="button"
                            className={`btn -alt ${cascading
                                ? 'cmem-delete-confirm' : 'cmem-delete-doc'}`}
                            onClick={confirm}>
                        {t(cascading
                            ? 'client_memory.documents.delete_confirm_all'
                            : 'client_memory.documents.delete_confirm_doc')}
                    </button>
                )}
            </ModalFooter>
        </Modal>
    );
}
